import {
  useEffect,
  useRef,
  useState,
} from "react";
import type {
  Experience,
  Interest,
  ParticipantAnswer,
  PerspectiveAnswer,
} from "../types";
import {
  EXPERIENCE_OPTIONS,
  INTEREST_OPTIONS,
} from "../types";

const INTEREST_LABELS: Record<Interest, string> = {
  need: "Need",
  yearn: "Yearn",
  want: "Want",
  open: "Open",
  dislike: "Dislike",
  "hard-limit": "Hard limit",
};

const EXPERIENCE_LABELS: Record<Experience, string> = {
  expert: "Expert",
  confident: "Confident",
  familiar: "Familiar",
  learning: "Learning",
  once: "Once",
  never: "Never",
};

export function optionLabel(value: Interest | Experience) {
  return value in INTEREST_LABELS
    ? INTEREST_LABELS[value as Interest]
    : EXPERIENCE_LABELS[value as Experience];
}

export function ResponseEditor({
  answer,
  onChange,
  onComplete,
  onNavigateHorizontal,
  autoFocus = true,
}: {
  answer: ParticipantAnswer;
  onChange: (answer: ParticipantAnswer) => void;
  onComplete?: (answer: ParticipantAnswer) => void;
  onNavigateHorizontal?: (direction: -1 | 1) => void;
  autoFocus?: boolean;
}) {
  const scaleRefs = useRef<Record<string, HTMLFieldSetElement | null>>({});
  const steps = Object.entries(answer.perspectives).flatMap(
    ([label, perspective]) =>
      perspective.enabled
        ? [
            { label, kind: "interest" as const },
            { label, kind: "experience" as const },
          ]
        : [],
  );

  const focusScale = (label: string, kind: "interest" | "experience") => {
    requestAnimationFrame(() => {
      const scale = scaleRefs.current[`${label}:${kind}`];
      if (!scale) return;
      scale.focus({ preventScroll: true });
      if (window.innerWidth <= 760) {
        scale.scrollIntoView({
          block: "center",
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
            .matches
            ? "auto"
            : "smooth",
        });
      }
    });
  };

  useEffect(() => {
    if (!autoFocus) return;
    const firstIncomplete = steps.find(
      ({ label, kind }) => answer.perspectives[label][kind] === null,
    );
    if (firstIncomplete) {
      focusScale(firstIncomplete.label, firstIncomplete.kind);
    }
  }, [answer.itemId]);

  const updatePerspective = (
    label: string,
    change: (perspective: PerspectiveAnswer) => PerspectiveAnswer,
  ) => {
    const next = {
      ...answer,
      perspectives: {
        ...answer.perspectives,
        [label]: change(answer.perspectives[label]),
      },
      updatedAt: Date.now(),
    };
    onChange(next);
    return next;
  };

  const choose = (
    label: string,
    kind: "interest" | "experience",
    value: Interest | Experience,
    advance = true,
  ) => {
    const next = updatePerspective(label, (current) => ({
      ...current,
      [kind]: value,
    }));
    if (!advance) return;
    const currentIndex = steps.findIndex(
      (step) => step.label === label && step.kind === kind,
    );
    const nextIncomplete =
      steps
        .slice(currentIndex + 1)
        .find(
          (step) => next.perspectives[step.label][step.kind] === null,
        ) ??
      steps
        .slice(0, currentIndex)
        .find(
          (step) => next.perspectives[step.label][step.kind] === null,
        );
    if (nextIncomplete) {
      focusScale(nextIncomplete.label, nextIncomplete.kind);
    } else {
      onComplete?.(next);
    }
  };

  const moveBetweenScales = (
    label: string,
    kind: "interest" | "experience",
    direction: -1 | 1,
  ) => {
    const currentIndex = steps.findIndex(
      (step) => step.label === label && step.kind === kind,
    );
    const target = steps[currentIndex + direction];
    if (target) {
      focusScale(target.label, target.kind);
    } else {
      onNavigateHorizontal?.(direction);
    }
  };

  const perspectiveCount = Object.keys(answer.perspectives).length;

  return (
    <div
      className={`response-editor ${perspectiveCount > 1 ? "multi" : "single"}`}
    >
      {Object.entries(answer.perspectives).map(([label, perspective]) => (
        <section
          className={`perspective-editor ${perspective.enabled ? "" : "disabled"}`}
          key={label}
        >
          <button
            type="button"
            className="perspective-toggle"
            aria-pressed={perspective.enabled}
            aria-label={`${perspective.enabled ? "Mark" : "Include"} ${label} ${
              perspective.enabled ? "not applicable" : "for this item"
            }`}
            onClick={() =>
              {
                const next = updatePerspective(label, (current) => ({
                  enabled: !current.enabled,
                  interest: current.enabled ? null : current.interest,
                  experience: current.enabled ? null : current.experience,
                }));
                const firstIncomplete = Object.entries(next.perspectives)
                  .flatMap(([nextLabel, nextPerspective]) =>
                    nextPerspective.enabled
                      ? [
                          { label: nextLabel, kind: "interest" as const },
                          { label: nextLabel, kind: "experience" as const },
                        ]
                      : [],
                  )
                  .find(
                    (step) =>
                      next.perspectives[step.label][step.kind] === null,
                  );
                if (firstIncomplete) {
                  focusScale(firstIncomplete.label, firstIncomplete.kind);
                }
              }
            }
          >
            {label}
          </button>
          {perspective.enabled ? (
            <div className="perspective-scales">
              <ChoiceScale
                kind="interest"
                value={perspective.interest}
                scaleKey={`${label}:interest`}
                register={(node) => {
                  scaleRefs.current[`${label}:interest`] = node;
                }}
                onChange={(interest, advance) =>
                  choose(label, "interest", interest, advance)
                }
                onMoveHorizontal={(direction) =>
                  moveBetweenScales(label, "interest", direction)
                }
              />
              <ChoiceScale
                kind="experience"
                value={perspective.experience}
                scaleKey={`${label}:experience`}
                register={(node) => {
                  scaleRefs.current[`${label}:experience`] = node;
                }}
                onChange={(experience, advance) =>
                  choose(label, "experience", experience, advance)
                }
                onMoveHorizontal={(direction) =>
                  moveBetweenScales(label, "experience", direction)
                }
              />
            </div>
          ) : (
            <p className="not-applicable">Not applicable for this item</p>
          )}
        </section>
      ))}
    </div>
  );
}

export function ResponseReadOnly({
  answer,
}: {
  answer: ParticipantAnswer;
}) {
  const perspectiveCount = Object.keys(answer.perspectives).length;
  return (
    <div
      className={`response-editor response-read-only ${perspectiveCount > 1 ? "multi" : "single"}`}
      aria-label="Read-only response"
    >
      {Object.entries(answer.perspectives).map(([label, perspective]) => (
        <section
          className={`perspective-editor ${perspective.enabled ? "" : "disabled"}`}
          key={label}
        >
          <span className="perspective-toggle">{label}</span>
          {perspective.enabled ? (
            <div className="perspective-scales">
              <ReadOnlyChoiceScale
                kind="interest"
                value={perspective.interest}
              />
              <ReadOnlyChoiceScale
                kind="experience"
                value={perspective.experience}
              />
            </div>
          ) : (
            <p className="not-applicable">Not applicable for this item</p>
          )}
        </section>
      ))}
    </div>
  );
}

function ReadOnlyChoiceScale({
  kind,
  value,
}: {
  kind: "interest";
  value: Interest | null;
} | {
  kind: "experience";
  value: Experience | null;
}) {
  const options =
    kind === "interest" ? INTEREST_OPTIONS : EXPERIENCE_OPTIONS;
  return (
    <fieldset
      className={`choice-scale ${kind}`}
      aria-label={`${kind === "interest" ? "Interest" : "Experience"}, read only`}
    >
      <legend className="sr-only">
        {kind === "interest" ? "Interest" : "Experience"}
      </legend>
      {options.map((option) => (
        <button
          type="button"
          tabIndex={-1}
          disabled
          key={option}
          className={value === option ? "selected" : ""}
          aria-pressed={value === option}
        >
          <span>{optionLabel(option)}</span>
        </button>
      ))}
    </fieldset>
  );
}

function ChoiceScale({
  kind,
  value,
  scaleKey,
  register,
  onChange,
  onMoveHorizontal,
}: {
  kind: "interest";
  value: Interest | null;
  scaleKey: string;
  register: (node: HTMLFieldSetElement | null) => void;
  onChange: (value: Interest, advance: boolean) => void;
  onMoveHorizontal: (direction: -1 | 1) => void;
} | {
  kind: "experience";
  value: Experience | null;
  scaleKey: string;
  register: (node: HTMLFieldSetElement | null) => void;
  onChange: (value: Experience, advance: boolean) => void;
  onMoveHorizontal: (direction: -1 | 1) => void;
}) {
  const options =
    kind === "interest" ? INTEREST_OPTIONS : EXPERIENCE_OPTIONS;
  const highlightedValueRef = useRef<Interest | Experience | null>(value);
  const [highlightedValue, setHighlightedValue] = useState<
    Interest | Experience | null
  >(value);

  useEffect(() => {
    highlightedValueRef.current = value;
    setHighlightedValue(value);
  }, [value]);

  return (
    <fieldset
      className={`choice-scale ${kind}`}
      data-scale={kind}
      data-scale-key={scaleKey}
      data-complete={value !== null}
      data-flow-control
      ref={register}
      tabIndex={0}
      aria-label={kind === "interest" ? "Interest" : "Experience"}
      onKeyDown={(event) => {
        const index = Number(event.key) - 1;
        if (index >= 0 && index < options.length) {
          event.preventDefault();
          event.stopPropagation();
          onChange(options[index] as never, true);
          return;
        }
        if (
          event.key === "Enter" ||
          event.key === " "
        ) {
          const highlighted = highlightedValueRef.current;
          if (highlighted === null) return;
          event.preventDefault();
          event.stopPropagation();
          onChange(highlighted as never, true);
          return;
        }
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();
          event.stopPropagation();
          onMoveHorizontal(event.key === "ArrowLeft" ? -1 : 1);
          return;
        }
        if (
          event.key === "ArrowUp" ||
          event.key === "ArrowDown" ||
          event.key === "Home" ||
          event.key === "End"
        ) {
          event.preventDefault();
          event.stopPropagation();
          const currentValue = highlightedValueRef.current;
          const currentIndex = currentValue
            ? options.indexOf(currentValue as never)
            : -1;
          const nextIndex =
            event.key === "Home"
              ? 0
              : event.key === "End"
                ? options.length - 1
                : event.key === "ArrowUp"
                  ? currentIndex <= 0
                    ? options.length - 1
                    : currentIndex - 1
                  : currentIndex >= options.length - 1
                    ? 0
                    : currentIndex + 1;
          const highlighted = options[nextIndex];
          highlightedValueRef.current = highlighted;
          setHighlightedValue(highlighted);
        }
      }}
    >
      <legend className="sr-only">
        {kind === "interest" ? "Interest" : "Experience"}
      </legend>
      {options.map((option, index) => {
        const label = optionLabel(option);
        return (
          <button
            type="button"
            tabIndex={-1}
            key={option}
            className={[
              value === option ? "selected" : "",
              highlightedValue === option ? "keyboard-highlighted" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-pressed={value === option}
            onClick={() => onChange(option as never, true)}
          >
            <span className="shortcut-hint" aria-hidden="true">
              {index + 1}
            </span>
            <span>{label}</span>
          </button>
        );
      })}
    </fieldset>
  );
}

const INTEREST_MAGNITUDE: Record<Interest, number> = {
  need: 5,
  yearn: 4,
  want: 3,
  open: 2,
  dislike: 1,
  "hard-limit": 0,
};

const EXPERIENCE_MAGNITUDE: Record<Experience, number> = {
  expert: 5,
  confident: 4,
  familiar: 3,
  learning: 2,
  once: 1,
  never: 0,
};

export function ResponseSummary({
  answer,
  compact = false,
}: {
  answer: ParticipantAnswer;
  compact?: boolean;
}) {
  return (
    <div className={`response-summary ${compact ? "compact" : ""}`}>
      {Object.entries(answer.perspectives).map(([label, perspective]) => (
        <section className="summary-perspective" key={label}>
          <h3>{label}</h3>
          {perspective.enabled ? (
            <>
              {perspective.interest ? (
                <Magnitude
                  kind="interest"
                  label={optionLabel(perspective.interest)}
                  value={INTEREST_MAGNITUDE[perspective.interest]}
                  hardLimit={perspective.interest === "hard-limit"}
                />
              ) : (
                <UnansweredScale kind="interest" />
              )}
              {perspective.experience ? (
                <Magnitude
                  kind="experience"
                  label={optionLabel(perspective.experience)}
                  value={EXPERIENCE_MAGNITUDE[perspective.experience]}
                />
              ) : (
                <UnansweredScale kind="experience" />
              )}
            </>
          ) : (
            <p className="summary-na">Not applicable</p>
          )}
        </section>
      ))}
    </div>
  );
}

export function compactAnswerSummary(answer: ParticipantAnswer) {
  return Object.entries(answer.perspectives)
    .filter(([, perspective]) => perspective.enabled)
    .map(([label, perspective]) => {
      const interest = perspective.interest
        ? optionLabel(perspective.interest)
        : "Unanswered";
      const experience = perspective.experience
        ? optionLabel(perspective.experience)
        : "Unanswered";
      return `${label}: ${interest} · ${experience}`;
    })
    .join("; ");
}

export function InlineAnswerSummary({
  answer,
  participant,
  hideParticipant = false,
}: {
  answer: ParticipantAnswer;
  participant: string;
  hideParticipant?: boolean;
}) {
  const enabledPerspectives = Object.entries(answer.perspectives).filter(
    ([, perspective]) => perspective.enabled,
  );
  return (
    <span className="inline-answer-summary">
      <strong className={hideParticipant ? "sr-only" : undefined}>
        {participant}
      </strong>
      {enabledPerspectives.length > 0 ? (
        enabledPerspectives.map(([label, perspective]) => (
          <span className="inline-summary-perspective" key={label}>
            <em>{label}</em>
            {perspective.interest ? (
              <Magnitude
                kind="interest"
                label={optionLabel(perspective.interest)}
                value={INTEREST_MAGNITUDE[perspective.interest]}
                hardLimit={perspective.interest === "hard-limit"}
              />
            ) : null}
            {perspective.experience ? (
              <Magnitude
                kind="experience"
                label={optionLabel(perspective.experience)}
                value={EXPERIENCE_MAGNITUDE[perspective.experience]}
              />
            ) : null}
          </span>
        ))
      ) : (
        <span className="inline-summary-na">Not applicable</span>
      )}
    </span>
  );
}

function Magnitude({
  kind,
  label,
  value,
  hardLimit = false,
}: {
  kind: "interest" | "experience";
  label: string;
  value: number;
  hardLimit?: boolean;
}) {
  return (
    <div
      className={`magnitude ${kind} ${hardLimit ? "hard-limit" : ""} ${
        kind === "interest" && value === 5 ? "need" : ""
      }`}
      aria-label={`${kind}: ${label}${hardLimit ? "" : `, ${value} of 5`}`}
    >
      <span className="magnitude-kind" aria-hidden="true">
        {kind === "interest" ? "Interest" : "Experience"}
      </span>
      <span className="magnitude-label">{label}</span>
      <span className="magnitude-meter" aria-hidden="true">
        {Array.from({ length: 5 }, (_, index) => (
          <i className={index < value ? "filled" : ""} key={index} />
        ))}
      </span>
    </div>
  );
}

function UnansweredScale({ kind }: { kind: "interest" | "experience" }) {
  return (
    <div className="magnitude unanswered" aria-label={`${kind}: unanswered`}>
      <span className="magnitude-kind" aria-hidden="true">
        {kind === "interest" ? "Interest" : "Experience"}
      </span>
      <span className="magnitude-label">Unanswered</span>
      <span className="magnitude-meter" aria-hidden="true">
        {Array.from({ length: 5 }, (_, index) => (
          <i key={index} />
        ))}
      </span>
    </div>
  );
}
